<template>
  <div class="home">
    <div class="kpi-row">
      <div class="kpi">
        <div class="kpi__label">{{ $t('dashboardPage.kpiLeadsStats') }}</div>
        <div class="kpi__triple">
          <div><span>{{ $t('dashboardPage.kpiLeadsWeek') }}</span><b>{{ dash.leadsCreatedThisWeek }}</b></div>
          <div><span>{{ $t('dashboardPage.kpiLeadsMonth') }}</span><b>{{ dash.leadsCreatedMtd }}</b></div>
          <div><span>{{ $t('dashboardPage.kpiLeadsYear') }}</span><b>{{ dash.leadsCreatedYtd }}</b></div>
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__label">{{ $t('dashboardPage.kpiQuotesPending') }}</div>
        <div class="kpi__value">{{ dash.quotesPendingInternalApproval }}</div>
      </div>
      <div class="kpi">
        <div class="kpi__label">{{ $t('dashboardPage.kpiContractsAwaiting') }}</div>
        <div class="kpi__value">{{ dash.contractsAwaitingSignature }}</div>
      </div>
      <div class="kpi">
        <div class="kpi__label">{{ $t('dashboardPage.kpiSignedValue') }}</div>
        <div class="kpi__value kpi__value--sm">{{ formatUsd(dash.signedContractValueMtdUsd) }}</div>
      </div>
    </div>

    <div class="grid">
      <section class="panel">
        <div class="panel__head">
          <h3>{{ $t('dashboardPage.pipelineTitle') }}</h3>
          <span v-if="dash.timezone">{{ dash.timezone }}</span>
        </div>
        <div class="stats">
          <div><span>{{ $t('dashboardPage.statLeadsMtd') }}</span><b>{{ dash.leadsCreatedMtd }}</b></div>
          <div><span>{{ $t('dashboardPage.statContractsMtd') }}</span><b>{{ dash.contractsSignedMtd }}</b></div>
          <div>
            <span>{{ $t('dashboardPage.statConversion') }}</span>
            <b>{{ dash.leadToSignedContractRateMtdPercent == null ? '—' : dash.leadToSignedContractRateMtdPercent.toFixed(1) + '%' }}</b>
          </div>
        </div>
      </section>

      <section class="panel">
        <div class="panel__head"><h3>{{ $t('dashboardPage.tasksTitle') }}</h3></div>
        <div v-if="workbench.tasks.length" class="list">
          <button v-for="task in workbench.tasks" :key="task.type" type="button" class="list-row" @click="$router.push(task.route)">
            <span>{{ task.title }} · {{ task.count }}</span>
            <i class="el-icon-arrow-right"></i>
          </button>
        </div>
        <el-empty v-else :description="$t('common.empty')" :image-size="56" />
      </section>

      <section class="panel">
        <div class="panel__head"><h3>{{ $t('dashboardPage.actionsTitle') }}</h3></div>
        <div class="actions">
          <el-button size="small" type="primary" plain @click="$router.push('/lead/new')">{{ $t('dashboardPage.gotoLeads') }}</el-button>
          <el-button size="small" type="primary" plain @click="$router.push('/quote/new')">{{ $t('dashboardPage.gotoQuote') }}</el-button>
          <el-button size="small" @click="$router.push('/schedule/calendar')">{{ $t('dashboardPage.gotoSchedule') }}</el-button>
          <el-button size="small" @click="$router.push('/finance/overview')">{{ $t('dashboardPage.gotoFinance') }}</el-button>
          <el-button size="small" @click="$router.push('/contract/list')">{{ $t('dashboardPage.gotoContracts') }}</el-button>
          <el-button size="small" @click="$router.push('/report/dashboard')">{{ $t('dashboardPage.gotoReports') }}</el-button>
          <el-button size="small" @click="$router.push('/report/sales-person')">{{ $t('dashboardPage.gotoSalesPersonReport') }}</el-button>
        </div>
      </section>

      <section class="panel">
        <div class="panel__head">
          <h3>{{ $t('dashboardPage.salesReportCardTitle') }}</h3>
          <el-button type="text" size="mini" @click="$router.push('/report/sales-person')">{{ $t('dashboardPage.salesReportOpenFull') }}</el-button>
        </div>
        <template v-if="isOfficeLike">
          <el-table v-if="workbench.salesPendingBreakdown.length" :data="workbench.salesPendingBreakdown" size="small">
            <el-table-column prop="salesName" :label="$t('dashboardPage.salesReportColSales')" min-width="120" />
            <el-table-column prop="quotesPending" :label="$t('dashboardPage.salesReportColQuotes')" width="90" align="center" />
            <el-table-column prop="contractsPending" :label="$t('dashboardPage.salesReportColContracts')" width="90" align="center" />
            <el-table-column :label="$t('dashboardPage.salesReportColActions')" width="80" align="center">
              <template slot-scope="scope">
                <el-button type="text" size="small" @click="openSalesPersonDetail(scope.row)">{{ $t('dashboardPage.salesReportViewDetail') }}</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-empty v-else :description="$t('common.empty')" :image-size="56" />
        </template>
        <template v-else>
          <el-button size="small" type="primary" plain @click="$router.push('/report/sales-person')">{{ $t('dashboardPage.salesReportOpenFull') }}</el-button>
        </template>
      </section>

      <section class="panel">
        <div class="panel__head"><h3>{{ $t('schedulePage.upcoming') }}</h3></div>
        <div v-if="workbench.upcomingVisits.length" class="list">
          <button v-for="visit in workbench.upcomingVisits" :key="visit.id" type="button" class="list-row" @click="$router.push('/lead/detail/' + visit.id)">
            <span>{{ visit.visitTitle || visit.leadNo }} · {{ visit.appointmentDate || '—' }}</span>
            <i class="el-icon-arrow-right"></i>
          </button>
        </div>
        <el-empty v-else :description="$t('common.empty')" :image-size="56" />
      </section>

      <section class="panel">
        <div class="panel__head">
          <h3>{{ $t('dashboardPage.calendarTitle') }}</h3>
          <span v-if="googleStatus.connected" class="tag tag--ok">{{ $t('dashboardPage.calendarConnected') }}</span>
          <span v-else-if="!googleLoading" class="tag">{{ $t('dashboardPage.calendarDisconnected') }}</span>
        </div>
        <p v-if="googleStatus.connected && googleStatus.calendarSummary" class="hint">{{ googleStatus.calendarSummary }}</p>
        <p v-if="googleStatus.probeError" class="warn">{{ googleStatus.probeError }}</p>
        <div class="actions">
          <el-button
            v-if="!googleStatus.connected"
            size="small"
            type="primary"
            plain
            :loading="googlePrepareLoading"
            @click="startGoogleOAuth"
          >{{ $t('dashboardPage.connectCalendar') }}</el-button>
          <el-button
            v-else
            size="small"
            type="danger"
            plain
            :loading="googleDisconnectLoading"
            @click="disconnectGoogle"
          >{{ $t('dashboardPage.disconnectCalendar') }}</el-button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { getDashboardSummary, getDashboardWorkbench } from '@/api/dashboard'
import {
  prepareGoogleOAuth,
  getGoogleIntegrationStatus,
  disconnectGoogleCalendar
} from '@/api/googleIntegration'
import { formatUsd } from '@/utils/formatUsd'
import { formatDisplayDateTime } from '@/utils/formatDisplayDateTime'

export default {
  name: 'Home',
  data() {
    return {
      isOfficeLike: false,
      dashLoading: false,
      dash: {
        timezone: '',
        leadsCreatedThisWeek: 0,
        leadsCreatedYtd: 0,
        quotesPendingInternalApproval: 0,
        contractsAwaitingSignature: 0,
        signedContractValueMtdUsd: 0,
        leadsCreatedMtd: 0,
        contractsSignedMtd: 0,
        leadToSignedContractRateMtdPercent: null
      },
      googleLoading: false,
      googlePrepareLoading: false,
      googleDisconnectLoading: false,
      googleStatus: {
        connected: false,
        configured: false,
        calendarSummary: null,
        probeError: null
      },
      workbench: {
        badges: {},
        tasks: [],
        recentActivities: [],
        salesPendingBreakdown: [],
        upcomingVisits: []
      }
    }
  },
  created() {
    const roles = (this.$store.state.userInfo && this.$store.state.userInfo.roleCodes) || []
    this.isOfficeLike = roles.includes('OFFICE') || roles.includes('ADMIN')
    this.handleGoogleQuery()
    this.loadAll()
  },
  watch: {
    '$route.query'() {
      this.handleGoogleQuery()
    }
  },
  methods: {
    formatUsd,
    openSalesPersonDetail(row) {
      const q = {}
      if (row && row.salesUserId == null) {
        q.onlyUnassigned = '1'
      } else if (row && row.salesUserId != null) {
        q.salesUserId = String(row.salesUserId)
      }
      this.$router.push({ path: '/report/sales-person', query: q })
    },
    formatActivityAt(at) {
      const s = formatDisplayDateTime(at)
      return s || '—'
    },
    async loadAll() {
      await Promise.all([this.loadDash(), this.loadWorkbench(), this.loadGoogleStatus()])
    },
    async loadDash() {
      this.dashLoading = true
      try {
        const raw = await getDashboardSummary()
        const d = raw.data !== undefined ? raw.data : raw
        this.dash = {
          timezone: d.timezone || '',
          leadsCreatedThisWeek: d.leadsCreatedThisWeek != null ? d.leadsCreatedThisWeek : 0,
          leadsCreatedYtd: d.leadsCreatedYtd != null ? d.leadsCreatedYtd : 0,
          quotesPendingInternalApproval: d.quotesPendingInternalApproval != null ? d.quotesPendingInternalApproval : 0,
          contractsAwaitingSignature: d.contractsAwaitingSignature != null ? d.contractsAwaitingSignature : 0,
          signedContractValueMtdUsd: d.signedContractValueMtdUsd != null ? d.signedContractValueMtdUsd : 0,
          leadsCreatedMtd: d.leadsCreatedMtd != null ? d.leadsCreatedMtd : 0,
          contractsSignedMtd: d.contractsSignedMtd != null ? d.contractsSignedMtd : 0,
          leadToSignedContractRateMtdPercent: d.leadToSignedContractRateMtdPercent
        }
      } catch (e) {
        /* keep zeros */
      } finally {
        this.dashLoading = false
      }
    },
    async loadWorkbench() {
      try {
        const raw = await getDashboardWorkbench()
        const data = raw.data !== undefined ? raw.data : raw
        this.workbench = {
          badges: data.badges || {},
          tasks: data.tasks || [],
          recentActivities: data.recentActivities || [],
          salesPendingBreakdown: data.salesPendingBreakdown || [],
          upcomingVisits: data.upcomingVisits || []
        }
        this.$store.dispatch('updateWorkbench', this.workbench)
      } catch (e) {
        this.workbench = { badges: {}, tasks: [], recentActivities: [], salesPendingBreakdown: [], upcomingVisits: [] }
      }
    },
    handleGoogleQuery() {
      const q = this.$route.query || {}
      if (q.google === 'connected') {
        this.$message.success(this.$t('dashboardPage.googleConnectedToast'))
        this.$router.replace({ path: '/home', query: {} })
        this.loadGoogleStatus()
      } else if (q.google === 'error') {
        const r = q.reason ? decodeURIComponent(String(q.reason)) : this.$t('dashboardPage.googleAuthUnknownReason')
        this.$message.error(`${this.$t('dashboardPage.googleAuthErrorPrefix')} ${r}`)
        this.$router.replace({ path: '/home', query: {} })
      }
    },
    async loadGoogleStatus() {
      this.googleLoading = true
      try {
        const raw = await getGoogleIntegrationStatus()
        const d = raw.data !== undefined ? raw.data : raw
        this.googleStatus = {
          connected: !!d.connected,
          configured: !!d.configured,
          calendarSummary: d.calendarSummary || null,
          probeError: d.probeError || null
        }
      } catch (e) {
        this.googleStatus = { connected: false, configured: false, calendarSummary: null, probeError: null }
      } finally {
        this.googleLoading = false
      }
    },
    async startGoogleOAuth() {
      this.googlePrepareLoading = true
      try {
        const raw = await prepareGoogleOAuth()
        if (raw.code && raw.code !== 200) {
          this.$message.error(raw.msg || this.$t('dashboardPage.oauthCannotStart'))
          return
        }
        const url = raw.authUrl || (raw.data && raw.data.authUrl)
        if (!url) {
          this.$message.error(this.$t('dashboardPage.noAuthUrlReturned'))
          return
        }
        window.location.href = url
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      } finally {
        this.googlePrepareLoading = false
      }
    },
    async disconnectGoogle() {
      try {
        await this.$confirm(this.$t('dashboardPage.disconnectGoogleConfirm'), this.$t('dashboardPage.disconnectGoogleTitle'), { type: 'warning' })
      } catch (e) {
        return
      }
      this.googleDisconnectLoading = true
      try {
        await disconnectGoogleCalendar()
        this.$message.success(this.$t('dashboardPage.disconnectedToast'))
        await this.loadGoogleStatus()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('dashboardPage.disconnectFailed'))
      } finally {
        this.googleDisconnectLoading = false
      }
    }
  }
}
</script>

<style scoped>
.home {
  padding: 20px 24px 36px;
}

.kpi-row {
  display: grid;
  grid-template-columns: 1.4fr repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 12px;
}

.kpi, .panel {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow: var(--shadow-card);
}

.kpi {
  padding: 14px 16px;
}

.kpi__label {
  font-size: 12px;
  color: var(--color-text-muted);
  margin-bottom: 8px;
}

.kpi__value {
  font-size: 24px;
  font-weight: 500;
  color: var(--color-text);
  line-height: 1.2;
}

.kpi__value--sm {
  font-size: 18px;
}

.kpi__triple {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.kpi__triple span {
  display: block;
  font-size: 11px;
  color: var(--color-text-muted);
  margin-bottom: 4px;
}

.kpi__triple b {
  font-size: 20px;
  font-weight: 500;
  color: var(--color-text);
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.panel {
  padding: 14px 16px 16px;
  min-height: 160px;
}

.panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.panel__head h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text);
}

.panel__head span {
  font-size: 12px;
  color: var(--color-text-muted);
}

.stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.stats span {
  display: block;
  font-size: 11px;
  color: var(--color-text-muted);
  margin-bottom: 4px;
}

.stats b {
  font-size: 20px;
  font-weight: 500;
  color: var(--color-text);
}

.list {
  display: grid;
  gap: 6px;
}

.list-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border-soft);
  border-radius: 6px;
  background: var(--color-bg);
  color: var(--color-text);
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
}

.list-row:hover {
  border-color: var(--color-border);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  font-size: 11px;
  color: var(--color-text-muted);
}

.tag--ok {
  color: var(--color-brand);
}

.hint {
  margin: 0 0 10px;
  font-size: 13px;
  color: var(--color-text-sub);
}

.warn {
  margin: 0 0 10px;
  font-size: 12px;
  color: #d97706;
}

@media (max-width: 1100px) {
  .kpi-row, .grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 720px) {
  .home {
    padding: 14px 14px 28px;
  }

  .kpi-row, .grid {
    grid-template-columns: 1fr;
  }
}
</style>